<script setup>
import { ref, computed } from "vue";
import Icon from "@/components/Icon.vue";
import { getErrorMessage } from "@/utils/error-message";
import { currentLanguage } from "@/globals/google-translate";
import { streamTutorialAnswer, sendTutorialFeedback } from "@/services/tutorial.service";
import { searchSteps, entryById } from "@/views/tutorial/search.js";
import { renderAnswer } from "@/views/tutorial/render.js";
import { mdiCreation, mdiArrowRight, mdiCheck, mdiImageOutline } from "@mdi/js";

/*
 * "Get an instant answer first" — the tutorial assistant inside the live-chat
 * confirmation (App.vue), so a question the tutorial already answers does not
 * have to wait for a person.
 *
 * Same backend as the /tutorial Ask box (POST /tutorial/ask, streamed). Emits:
 *   asked    { question, id }  — so App.vue can hand the question to the chat
 *                                 if the customer still starts one
 *   resolved                   — "That answered it": rated helpful, dialog closes
 *   open-step stepId           — the step with screenshots, on the tutorial page
 */
const emit = defineEmits(["asked", "resolved", "open-step"]);

const question = ref("");
const input = ref(null);
const status = ref("idle"); // idle | loading | streaming | done | error
const text = ref("");
const html = ref("");
const error = ref("");
const id = ref(null);
const steps = ref([]);
const asked = ref("");

const best = computed(() => entryById(steps.value[0]));
const busy = computed(() => status.value === "loading" || status.value === "streaming");

async function ask() {
  const q = question.value.trim();
  if (!q || busy.value) return;
  asked.value = q;
  status.value = "loading";
  text.value = "";
  html.value = "";
  error.value = "";
  id.value = null;
  steps.value = searchSteps(q).map((e) => e.id);
  emit("asked", { question: q, id: null });

  const lang = currentLanguage();
  try {
    const done = await streamTutorialAnswer(
      { question: q, history: [], ...(lang && lang !== "en" ? { lang } : {}) },
      {
        delta(chunk) {
          status.value = "streaming";
          text.value += chunk;
          html.value = renderAnswer(text.value, { partial: true });
        },
      }
    );
    text.value = done.answer || text.value;
    html.value = renderAnswer(text.value);
    id.value = done.id ?? null;
    const cited = (done.steps || []).filter((s) => entryById(s));
    if (cited.length) steps.value = cited;
    status.value = "done";
    emit("asked", { question: q, id: id.value });
  } catch (e) {
    error.value = getErrorMessage(e, "The instant answer is unavailable right now — start the chat and we'll help.");
    status.value = "error";
  }
}

async function resolved() {
  if (id.value) {
    try {
      await sendTutorialFeedback(id.value, true);
    } catch {
      /* Best effort. */
    }
  }
  emit("resolved");
}

function onAnswerClick(event) {
  const link = event.target.closest("[data-step]");
  if (!link) return;
  event.preventDefault();
  emit("open-step", link.dataset.step);
}

defineExpose({ focus: () => input.value?.focus() });
</script>

<template>
  <!-- One quiet field; the answer appears under it as plain text. Kept
       deliberately light: it sits inside a dialog that is about starting a
       chat, and should not compete with it. -->
  <div data-testid="chat-quick-ask">
    <form class="relative" @submit.prevent="ask">
      <label for="quick-ask-question" class="sr-only">Your question</label>
      <icon
        :path="mdiCreation"
        :size="16"
        w="w-4"
        h="h-4"
        class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-brand-500"
      />
      <input
        id="quick-ask-question"
        ref="input"
        v-model="question"
        type="text"
        maxlength="500"
        autocomplete="off"
        placeholder="Ask the tutorial first…"
        class="input w-full pl-9 pr-11"
      />
      <button
        type="submit"
        class="absolute right-1.5 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-brand-600 transition-colors hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-40 dark:text-brand-400 dark:hover:bg-gray-700"
        :disabled="!question.trim() || busy"
        aria-label="Ask"
      >
        <icon :path="mdiArrowRight" :size="16" w="w-4" h="h-4" />
      </button>
    </form>

    <div v-if="status !== 'idle'" class="mt-3" aria-live="polite">
      <p v-if="status === 'loading'" class="animate-pulse text-sm text-gray-400" aria-busy="true">Looking it up…</p>
      <div
        v-else-if="status === 'streaming' || status === 'done'"
        class="tut-quick-answer max-h-40 overflow-y-auto text-sm text-gray-700 dark:text-gray-200"
        data-testid="chat-quick-answer"
        @click="onAnswerClick"
        v-html="html"
      />
      <p v-else-if="status === 'error'" class="text-sm text-gray-500 dark:text-gray-400" role="alert">{{ error }}</p>

      <div v-if="status === 'done'" class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <button type="button" class="link inline-flex items-center gap-1 font-medium" @click="resolved">
          <icon :path="mdiCheck" :size="14" w="w-4" h="h-4" />
          That answered it
        </button>
        <button
          v-if="best"
          type="button"
          class="link inline-flex items-center gap-1"
          :title="best.title"
          @click="emit('open-step', best.id)"
        >
          <icon :path="mdiImageOutline" :size="14" w="w-4" h="h-4" />
          See screenshots
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tut-quick-answer :deep(p + p),
.tut-quick-answer :deep(p + ul),
.tut-quick-answer :deep(p + ol),
.tut-quick-answer :deep(ul + p),
.tut-quick-answer :deep(ol + p) {
  margin-top: 0.5rem;
}
.tut-quick-answer :deep(ul) {
  list-style: disc;
  padding-left: 1.25rem;
}
.tut-quick-answer :deep(ol) {
  list-style: decimal;
  padding-left: 1.25rem;
}
</style>
